import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { catalogApi } from '../../api/endpoints/catalog';
import type { Permission } from '../../api/types/permissions';

/**
 * Vista READ-ONLY de los permisos de un rol. Se usa en el modal "Ver permisos" de RolesTable, para
 * TODOS los roles (Sistema y Custom).
 *
 * No se reusa `PermissionPicker` porque ese componente lista el catálogo COMPLETO con checkboxes
 * editables (no acepta read-only ni un value acotado a un rol). Acá mostramos SOLO las keys del rol,
 * agrupadas por namespace (el prefijo antes del primer `.`: `tenant.*`, `agents.*`, …) y, dentro de
 * cada grupo, en orden ALFABÉTICO (convención de presentación de Fiado). Enriquecemos cada key con el
 * `displayName` del catálogo cuando está disponible; si no, mostramos la key cruda como fallback.
 */

interface Props {
    permissions: Permission[];
}

/** Namespace = primer segmento de la key (`tenant.user.create` → `tenant`). */
function namespaceOf(key: string): string {
    const i = key.indexOf('.');
    return i === -1 ? key : key.slice(0, i);
}

export function RolePermissionsView({ permissions }: Props) {
    // El catálogo es opcional: si carga, mejora la legibilidad con displayName; si no, key cruda.
    const { data } = useQuery({
        queryKey: ['catalog', 'permissions'],
        queryFn: catalogApi.listPermissions,
        staleTime: 5 * 60_000,
    });

    const labelByKey = useMemo(() => {
        const map = new Map<string, string>();
        for (const p of data?.items ?? []) {
            if (p.displayName) map.set(p.key, p.displayName);
        }
        return map;
    }, [data]);

    const groups = useMemo(() => {
        const buckets = new Map<string, Permission[]>();
        for (const key of permissions) {
            const ns = namespaceOf(key);
            const bucket = buckets.get(ns);
            if (bucket) bucket.push(key);
            else buckets.set(ns, [key]);
        }
        return [...buckets.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([namespace, keys]) => ({
                namespace,
                keys: [...keys].sort((a, b) => a.localeCompare(b)),
            }));
    }, [permissions]);

    if (permissions.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">Este rol no tiene permisos asignados.</p>
        );
    }

    return (
        <div className="space-y-4">
            {groups.map(({ namespace, keys }) => (
                <section key={namespace} className="space-y-2">
                    <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                        {namespace}
                    </h3>
                    <ul className="space-y-1 pl-3 border-l">
                        {keys.map((key) => (
                            <li key={key} className="flex flex-col">
                                <span className="text-sm font-medium leading-tight">
                                    {labelByKey.get(key) ?? key}
                                </span>
                                {labelByKey.has(key) && (
                                    <span className="font-mono text-xs text-muted-foreground leading-tight">
                                        {key}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
}
